import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faComments } from '@fortawesome/free-solid-svg-icons';
import SupportFlowWidget from './SupportFlowWidget';
import SupportSummaryCard from './SupportSummaryCard';
import { SupportSessionState, SupportStepPayload } from '../../types/chat';

interface SupportSessionMessageProps {
    session: SupportSessionState;
    submitting?: boolean;
    onSubmitStep: (payload: SupportStepPayload, submission: { stepId: string; answer: any }) => void;
    variant?: 'default' | 'standard';
}

const renderAnswerText = (answer: string) => {
    return answer.split('\n').map((line, idx) => (
        <p key={idx} className="text-sm text-gray-700 whitespace-pre-wrap">
            {line}
        </p>
    ));
};

const SupportSessionMessage: React.FC<SupportSessionMessageProps> = ({
    session,
    submitting = false,
    onSubmitStep,
    variant = 'default'
}) => {
    const {
        title,
        description,
        introText,
        responses,
        currentStep,
        summary,
        awaitingNext,
        finalMessage,
        status
    } = session;

    const handleSubmit = async (submission: { stepId: string; answer: any }) => {
        if (!currentStep) return;
        await onSubmitStep(currentStep, submission);
    };

    const Container: React.FC<{ children: React.ReactNode }> = ({ children }) => {
        if (variant === 'standard') {
            return (
                <div className="std-support-session" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {children}
                </div>
            );
        }
        return (
            <div className="rounded-2xl border border-blue-200 bg-white p-6 shadow-sm">
                {children}
            </div>
        );
    };

    const Heading: React.FC<{ children: React.ReactNode }> = ({ children }) => {
        if (variant === 'standard') {
            return (
                <div className="flex items-start gap-2 text-gray-700 text-sm mb-3">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                        <FontAwesomeIcon icon={faComments} />
                    </span>
                    <div className="flex-1">{children}</div>
                </div>
            );
        }
        return <div className="mb-4">{children}</div>;
    };

    const SectionWrapper: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => {
        if (variant === 'standard') {
            return (
                <div
                    className={`std-support-section ${className}`}
                    style={{
                        background: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        borderRadius: 12,
                        padding: 12
                    }}
                >
                    {children}
                </div>
            );
        }
        return <div className={`rounded-xl border border-gray-200 bg-gray-50/60 p-4 ${className}`}>{children}</div>;
    };

    return (
        <Container>
            <Heading>
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <h3 className="text-base font-semibold text-gray-900">{title || 'Troubleshooting Assistant'}</h3>
                        {description && <p className="text-xs text-gray-500">{description}</p>}
                    </div>
                    {currentStep && (
                        <span className="text-xs font-medium uppercase tracking-wide text-blue-600">
                            Step {currentStep.step_index || '?'} of {currentStep.total_steps || '?'}
                        </span>
                    )}
                </div>
                {/* Only show introText if there's no finalMessage yet (during streaming) */}
                {introText && !finalMessage && (
                    <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{introText}</p>
                )}
            </Heading>

            {responses.length > 0 && (
                <SectionWrapper className="mb-3">
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">What you shared</h4>
                    <div className="space-y-3">
                        {responses.map((response) => (
                            <div key={response.stepId} className="rounded-lg bg-white/70 p-3 border border-gray-200">
                                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    {response.question}
                                </p>
                                <div className="mt-1">{renderAnswerText(response.answer)}</div>
                            </div>
                        ))}
                    </div>
                </SectionWrapper>
            )}

            {currentStep && (
                <SectionWrapper className="mb-3">
                    <SupportFlowWidget
                        payload={currentStep}
                        onSubmit={handleSubmit}
                        submitting={submitting}
                    />
                </SectionWrapper>
            )}

            {!currentStep && awaitingNext && status !== 'completed' && (
                <SectionWrapper className="mb-3 text-sm text-gray-500 flex items-center gap-2">
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Looking up what to ask next…</span>
                </SectionWrapper>
            )}

            {summary && (
                <SectionWrapper className="mb-3">
                    <SupportSummaryCard summary={summary} />
                </SectionWrapper>
            )}

            {finalMessage && (
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{finalMessage}</p>
            )}
        </Container>
    );
};

export default SupportSessionMessage;
